import { Directive, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BehaviorSubject, debounceTime, filter } from 'rxjs';
import { OFFLINE_MEDIA_PREFIX } from '../constants';
import { TokenApiService } from '../services';
import { CloudImageService } from '../services/cloud-image.service';
import { SharedLibraryService } from '../services/shared-library.service';


@Directive({
    /* tslint:disable-next-line */
    selector: '[cropImage]',
    standalone: false
})
export class CropImageDirective implements OnInit {
  @Input() public dynamicWidth = true;
  @Input() public dynamicWidthCoeff = 1;

  @Input() public set cropImage(value: string | null) {
    if (this._cropImage === value) {
      return;
    }

    this._cropImage = value;
    this.mediaChanged.next(true);
  }

  public get cropImage() {
    return this._cropImage || '';
  }

  @Input() crop: any;
  @Input() imageWidth: any;
  @Input() clearBackgroundWhenEmpty = false;

  @Input() set additionalOptions(value: any) {
    if (!value) {
      return;
    }

    this._additionalOptions = { ...value, width: value?.w || value?.width, height: value?.h || value.height, w: null, h: null };
    Object.keys(this._additionalOptions).forEach((key) => this._additionalOptions[key] == null && delete this._additionalOptions[key]);
    this.mediaChanged.next(true);
  };

  public get additionalOptions() {
    return this._additionalOptions;
  }

  @Input() disabled = false;
  @Input() offlineImage = false;
  @Input() private = false;
  @Input() public applyFullModeRatio = false;
  @Input() imageId?: string; // requried if private = true
  @Input() setContainerRatioAndSize = false;
  @Output() public containerAspectRatioChanged: EventEmitter<string> = new EventEmitter();

  protected isFullMode = false;
  protected targetElement: HTMLElement;

  private _cropImage!: string | undefined;

  private readonly imageWidthRoundValue = 2;
  private readonly minWidth = 300;
  private _additionalOptions: { [key: string]: any } = {};

  private mediaChanged = new BehaviorSubject<boolean>(false);

  private resizeObs: ResizeObserver;

  constructor(
    protected sharedLibraryService: SharedLibraryService,
    protected el: ElementRef,
    protected tokenApiService: TokenApiService,
    protected cloudImageService: CloudImageService
  ) { }

  ngOnInit(): void {
    this.mediaChanged
      .pipe(
        filter(changed => changed),
        debounceTime(200)
      )
      .subscribe(() => this.initialize());
  }

  async initialize(): Promise<void> {
    const targetEl = this.getTargetElement();
  
    if (!this.cropImage) {
      if (this.clearBackgroundWhenEmpty) {
        targetEl.style.backgroundImage = '';
      }

      return;
    }

    //Handles images stored in device's filesystem
    const offlineImage = this.isOffline(this.cropImage);
    if (offlineImage) {

      this.setImageOnTarget(targetEl, offlineImage);
      return;
    }

    const base64regex = /^(?:[data]{4}:(text|image|application)\/[a-z]*)/;
    const isBase64 = base64regex.test(this.cropImage) || this.cropImage.startsWith('blob:');

    if (isBase64) {
      this.setImageOnTarget(targetEl, this.cropImage);
      return;
    }

    // fix when url is missing protocol
    if (this.cropImage.indexOf('//') === 0) {
      this.cropImage = `https:${this.cropImage}`;
    }

    if (this.disabled) {
      targetEl.style.backgroundImage = `url('${this.cropImage}')`;
      return;
    }

    let imageWidth = this.imageWidth;

    if (this.dynamicWidth && targetEl && !imageWidth) {
      const round = Math.pow(10, this.imageWidthRoundValue);
      const width = Math.ceil(this.elementOffsetWidth(targetEl?.parentElement) / round) * round;
      const coeff = this.dynamicWidthCoeff || 1;

      imageWidth = Math.max(width, this.minWidth) * coeff;
    }

    this.cloudImageService.generateCloudImageResourceUrl(this.cropImage, imageWidth, this.additionalOptions, this.crop, this.private, this.imageId)
      .subscribe(async result => {
        if (targetEl instanceof HTMLImageElement) {
          this.handleImageLoad(targetEl);
          targetEl.src = result;
          return;
        }
    
        const shouldPreload = this.setContainerRatioAndSize || this.isFullMode;

        if (shouldPreload) {
          await this.preloadAndMeasure(result, targetEl);
        }
    
        targetEl.style.backgroundImage = `url('${result}')`;  
      }); 
  }

  private getFirstParentWithNotEmptyWidth(element: HTMLElement): HTMLElement {
    if (!element) {
      return null;
    }

    if (element.offsetWidth) {
      return element;
    }

    return this.getFirstParentWithNotEmptyWidth(element.parentElement);
  }

  private elementOffsetWidth(element: HTMLElement | null): number {
    if (!element) {
      return 0;
    }

    if (element.offsetWidth >= this.minWidth) {
      return element.offsetWidth;
    }

    return this.elementOffsetWidth(element.parentElement);
  }

  protected handleImageLoad(element: HTMLImageElement): void {
    const update = () => this.syncContainerAspectRatio(element);

    if (element.complete && element.naturalWidth) {
      update();
      return;
    }

    element.addEventListener('load', update);
  }

  protected preloadAndMeasure(src: string, element: HTMLElement): Promise<void> {
    element.style.minWidth = null;
    const parentElement = this.getFirstParentWithNotEmptyWidth(element);

    return new Promise(resolve => {
      const containerWidth = parentElement?.offsetWidth;

      const image = new Image();

      image.addEventListener('load', () => {
        if (this.setContainerRatioAndSize) {
          this.subscribeOnWidthChange(element, image, parentElement);
          this.setElementSizeBasedOnImage(image, element, containerWidth);
        }

        if (this.isFullMode) {
          this.syncContainerAspectRatio(image);
        }
        
        resolve();
      });

      image.src = src;
    });
  }

  protected getTargetElement(): HTMLElement {
    return this.targetElement || this.el.nativeElement;
  }

  protected subscribeOnWidthChange(nativeElement: HTMLElement, image: HTMLImageElement, parentElement: HTMLElement): void {
    if (this.resizeObs) {
      this.resizeObs.disconnect();
    }

    if (!parentElement) {
      return;
    }

    this.resizeObs = new ResizeObserver(() => {
      const containerWidth = parentElement.offsetWidth;

      this.setElementSizeBasedOnImage(image, nativeElement, containerWidth);
    });


    this.resizeObs.observe(parentElement);
  }

  protected setElementSizeBasedOnImage(image: HTMLImageElement, element: HTMLElement, containerWidth: number): void {
    const { width: originalWidth, height: originalHeight } = image;
    const ratio = containerWidth ? containerWidth / originalWidth : 1;
    const [width, height] = [originalWidth * ratio, originalHeight * ratio];

    if (!element.offsetWidth) {
      element.style.minWidth = `${Math.min(originalWidth, width)}px`
    }

    element.style.paddingTop = `${100 * height / width}%`;
    element.style.maxWidth = `${width}px`;
  }

  protected syncContainerAspectRatio(element: HTMLElement): void {
    const isImage = element instanceof HTMLImageElement;

    if (!isImage || !this.isFullMode) {
      this.clearContainerAspectRatio();
      return;
    }

    if (!element.complete) {
      return;
    }

    const ratio = `${element.naturalWidth} / ${element.naturalHeight}`;
    this.containerAspectRatioChanged.emit(ratio);
    this.el.nativeElement.style.aspectRatio = ratio;
  }

  protected clearContainerAspectRatio(): void {
    this.el.nativeElement.style.aspectRatio = null;
    this.containerAspectRatioChanged.emit(null);
  }

  protected getContainerVerticalPadding(): number {
    const computedStyle = window.getComputedStyle(this.el.nativeElement);
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    
    return paddingBottom + paddingTop;
  }

  protected setImageOnTarget(target: HTMLElement, url: string): void {
    if (target instanceof HTMLImageElement) {
      target.src = url;
      return;
    }

    target.style.backgroundImage = `url('${url}')`;
  }

/**
 * The function checks if a given URL starts with a specific prefix and returns the URL without the
 * prefix if it does, otherwise it returns false.
 * @param {string} url - The `url` parameter is a string that represents a URL.
 * @returns either a string or false.
 */
  private isOffline(url: string): string | false {
    const protocolToRemove = OFFLINE_MEDIA_PREFIX;

    if (url.startsWith(protocolToRemove)) {
      return url.slice(protocolToRemove.length);
    }

    return false;
  }
}
